import { getSessionToken } from '@tiendanube/nexo';
import axios from 'axios';
import nexo from '../nexoClient.js';

/**
 * Axios instance for the admin backend. Every request carries a fresh Nexo session
 * token as a Bearer credential (the official interceptor pattern). The backend verifies
 * the JWT and derives the store id from it; this replaces login.
 *
 * Dev fallback: when the admin runs OUTSIDE the Nuvemshop iframe (e.g. while Developer
 * Mode / "Test App" is not yet enabled on the store), getSessionToken never resolves
 * because there is no parent admin to answer. If VITE_DEV_STORE_ID is set, we fall back
 * to the backend's dev-only /dev/session-token endpoint so the app is fully usable
 * locally. This fallback is inert in production (no dev store id, endpoint not mounted).
 */
const baseURL = import.meta.env.VITE_BACKEND_URL;
const devStoreId = import.meta.env.VITE_DEV_STORE_ID;

const axiosInstance = axios.create({ baseURL });

let cachedDevToken: string | null = null;

async function fetchDevToken(): Promise<string> {
  if (cachedDevToken) return cachedDevToken;
  // Bare axios (not axiosInstance) to avoid re-triggering this interceptor.
  const { data } = await axios.get<{ token: string }>(`${baseURL}/dev/session-token`, {
    params: { store_id: devStoreId },
  });
  cachedDevToken = data.token;
  return data.token;
}

async function resolveToken(): Promise<string> {
  // Race the real Nexo token against a short timeout. Inside the iframe it resolves fast;
  // standalone it would hang, so we time out and use the dev fallback when configured.
  try {
    return await Promise.race([
      getSessionToken(nexo),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('nexo session token timeout')), 1500),
      ),
    ]);
  } catch (err) {
    if (devStoreId) return fetchDevToken();
    throw err;
  }
}

axiosInstance.interceptors.request.use(async (request) => {
  const token = await resolveToken();
  request.headers.Authorization = `Bearer ${token}`;
  return request;
});

export default axiosInstance;
